import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createBrowserRouter } from 'react-router';
import { ApiProblem, initI18n, adoptFirstTouchParam } from '@cowoo/shared';
import { hydrateSession } from './lib/auth';
import { routes } from './routes';
import './index.css';

initI18n('web');

// A token that survived the reload is adopted before the first render, so the
// guards do not bounce a logged-in user to `/login` on the way past.
void hydrateSession();

// § 14.3 — the site hands first-touch over as `?ft=`; adopt it and clean the URL.
adoptFirstTouchParam(window.location.search);
if (new URLSearchParams(window.location.search).has('ft')) {
  const url = new URL(window.location.href);
  url.searchParams.delete('ft');
  window.history.replaceState(null, '', url.toString());
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (count, error) =>
        !(error instanceof ApiProblem && error.status >= 400 && error.status < 500) && count < 2,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: { retry: 0 },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={createBrowserRouter(routes)} />
    </QueryClientProvider>
  </StrictMode>,
);
